import {
  Children,
  cloneElement,
  isValidElement,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import { cn } from "@/lib/cn";

export interface TimelineItemProps {
  bullet?: ReactNode;
  title?: ReactNode;
  /** Token de color de la paleta para el bullet + la línea que sale de él. */
  color?: string;
  children?: ReactNode;
  /** Inyectados por `<Timeline>` · no se pasan a mano. */
  bulletSize?: number;
  isLast?: boolean;
  lineActive?: boolean;
}

/** Un nivel/entrada del timeline · bullet + título + contenido, con la línea conectora hacia el siguiente item. */
export function TimelineItem({
  bullet,
  title,
  color = "slate",
  children,
  bulletSize = 20,
  isLast = false,
  lineActive = false,
}: TimelineItemProps) {
  return (
    <li className="relative flex gap-3 pb-6 last:pb-0">
      {!isLast && (
        <span
          className="absolute top-0 w-0.5"
          style={{
            left: bulletSize / 2 - 1,
            top: bulletSize,
            bottom: 0,
            backgroundColor: lineActive ? `var(--color-${color}-600)` : "var(--color-border)",
          }}
        />
      )}
      <span
        className="relative z-10 flex shrink-0 items-center justify-center rounded-full text-white"
        style={{ width: bulletSize, height: bulletSize, backgroundColor: `var(--color-${color}-600)` }}
      >
        {bullet}
      </span>
      <div className="min-w-0 flex-1 pb-1" style={{ paddingTop: (bulletSize - 16) / 2 }}>
        {title && <div className="leading-tight">{title}</div>}
        {children}
      </div>
    </li>
  );
}

export interface TimelineProps extends Omit<HTMLAttributes<HTMLUListElement>, "color"> {
  /** Índice hasta el cual (exclusivo) la línea conectora se pinta con el color del item. */
  active?: number;
  bulletSize?: number;
  /** Color default de los items que no traen su propio `color` · igual a Mantine (`primaryColor`). */
  color?: string;
  children: ReactNode;
}

/**
 * Reemplazo de `<Timeline>`/`<Timeline.Item>` de Mantine (usado por
 * `FlujoCard` y `HistorialSolicitudPage`) · timeline vertical con bullet +
 * línea conectora. Mismo shape de props (`active`, `bulletSize`, `color` en
 * el root; `bullet`/`title`/`color` en cada `Item`) para minimizar el diff
 * en los dos call-sites que lo consumen.
 */
export function Timeline({ active = -1, bulletSize = 20, color = "multiva", className, children, ...props }: TimelineProps) {
  const items = Children.toArray(children).filter(isValidElement) as ReactElement<TimelineItemProps>[];
  return (
    <ul className={cn("flex flex-col", className)} {...props}>
      {items.map((item, idx) =>
        cloneElement(item, {
          key: item.key ?? idx,
          bulletSize,
          isLast: idx === items.length - 1,
          lineActive: idx < active,
          color: item.props.color ?? color,
        })
      )}
    </ul>
  );
}

Timeline.Item = TimelineItem;
